<template>
    <div class="bg-white p-3 rounded-xl">
        <div role="status">
            <div v-if="loadingLocation" class="flex justify-center my-12">
                <div v-if="initLoad">
                    <Spinner class="text-primary w-8 h-8" aria-label="Loading map" />
                </div>
            </div>
            <div v-else>
                <span class="sr-only">Map loading complete</span>
            </div>
        </div>
        <div v-if="!loadingLocation" class="flex flex-col-reverse md:flex-row gap-2 text-gray-600">
            <div class="map-sidebar">
                <div v-if="selectedOutlet" class="p-1">
                    <button @click="selectedOutlet = null" class="flex items-center justify-center bg-primary-800 text-white w-full p-2 rounded hover:bg-primary-500 focus:bg-primary-800 focus:ring-2 focus:ring-offset-2 focus:ring-primary-800">
                        <Icon :icon="ICONS.chevronLeft" class="h-5" aria-hidden="true"/>
                        Back
                    </button>

                    <div class="mt-4 border-t p-3">
                        <h3 class="text-xl">{{ selectedOutlet.name }}</h3>
                        <div class="flex items-center mt-1">
                            <Icon :icon="ICONS.mapMarker" class="h-6 mr-2" />
                            {{ selectedOutlet.address }} {{ selectedOutlet.zip }}
                        </div>
                        <div class="mt-6">
                            <div class="font-bold">Purchase type:</div>
                            <ul class="list-disc list-inside leading-10">
                                <li>{{ selectedOutlet.purchase_type }}</li>
                            </ul>
                        </div>

                        <div class="flex gap-1 mt-4">
                            <a :href="urlForDirections(selectedOutlet)" class="inline-flex items-center space-x-2">
                                <Icon :icon="ICONS.directions" class="h-6" aria-label="Directions icon"/>
                                <span>Trip Planner</span>
                            </a>
                        </div>
                    </div>
                </div>
                <div v-else class="map-list">
                    <div class="relative">
                        <label for="where-to-buy-search">Type name, zip, or street</label>
                        <InputText
                            v-model="filters.searchTerm.value"
                            title="Type name, zip, or street"
                            placeholder="Name, zip, or street etc..."
                            class="w-full mt-2 mb-3"
                            name="where-to-buy-search"
                            id="where-to-buy-search"
                        />
                        <button @click="load({searchTerm: filters.searchTerm.value})" class="absolute right-0 top-0 h-10 w-10 flex items-center justify-center" aria-label="Search">
                            <Icon :icon="ICONS.magnify" class="w-5 h-5" aria-label="Magnifier icon"/>
                        </button>
                    </div>

                    <label for="where-to-buy-purchase-type">Select a purchase type</label>
                    <InputSelect
                        v-model="filters.purchaseType.value"
                        :options="purchaseTypes"
                        title="Select a purchase type"
                        aria-label="Select a purchase type"
                        class="w-full mt-2"
                        name="where-to-buy-purchase-type"
                        id="where-to-buy-purchase-type"
                    />

                    <div role="status">
                        <template v-for="(filter, key) in filters">
                            <div v-if="filter.value" class="bg-yellow-100 border border-yellow-500 text-yellow-800 p-3 mt-2">
                                <div class="flex">
                                    <span>
                                        {{ filter.label }} filter is <span class="font-bold">on</span>
                                    </span>
                                    <button @click="filters[key].value = null" class="ml-auto" :aria-label="`Reset ${filter.label} filter`">
                                        <Icon :icon="ICONS.close" class="w-6 h-6" role="presentation" />
                                    </button>
                                </div>
                            </div>
                        </template>
                        <div v-if="!isLoadingOutlets" class="sr-only">
                            <div v-if="outletList.length === 0">
                                There are no retail locations
                                <span v-if="filters.searchTerm.value">matching "{{ filters.searchTerm.value }}"</span>
                                <span v-if="filters.purchaseType.value"> with {{ filters.purchaseType.value }} fares</span>
                            </div>
                            <div v-if="filters.searchTerm.value && outletList.length !== 0">
                                Found <span>{{ outletList.length }}</span> results for "<span>{{ filters.searchTerm.value }}</span>"
                            </div>
                        </div>
                        <div v-else class="sr-only">
                            Loading outlets
                        </div>
                    </div>
                    <template v-if="!isLoadingOutlets">
                        <div v-if="outletList.length === 0" class="p-2 text-center mt-4">
                            There are no retail locations
                            <span v-if="filters.searchTerm.value">matching "{{ filters.searchTerm.value }}"</span>
                            <span v-if="filters.purchaseType.value"> with {{ filters.purchaseType.value }} fares</span>
                        </div>

                        <div v-else-if="filters.searchTerm.value" class="mt-4" aria-live="assertive">
                            Found <span class="font-bold">{{ outletList.length }}</span> results for "<span class="font-bold">{{ filters.searchTerm.value }}</span>"
                        </div>

                        <ul class="block mt-6">
                            <li v-for="outlet in outletList" :key="outlet.id" class="block">
                                <button :aria-label="`${outlet.name} at ${outlet.address} ${outlet.zip}`" class="w-full border-b border-gray-200 appearance-none p-3 cursor-pointer text-left hover:border-transparent hover:bg-primary-300 hover:text-white focus:bg-primary-300 focus:text-white focus:ring-2 focus:ring-offset-2 focus:ring-primary-300 " @click="selectedOutlet = outlet">
                                    <h3 class="block w-full text-lg mb-5" style="color: inherit">{{ outlet.name }}</h3>
                                    <span class="w-full flex items-center mt-1">
                                        <Icon :icon="ICONS.mapMarker" class="h-6 mr-1" aria-label="Map marker icon"/>{{ outlet.address }} {{ outlet.zip }}
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </template>
                    <div class="flex justify-center my-12">
                        <Spinner class="text-primary w-8 h-8" aria-label="Loading outlets" />
                    </div>
                </div>
            </div>
            <div class="map-container">
                <ValleyMap ref="valleyMap" :map-markers="markers" :map-center="mapCenter" :zoom="mapZoom" @clickedMarker="clickedMarker"/>
            </div>
        </div>
    </div>
</template>

<script>
import {usePage} from '@inertiajs/vue3';
import {computed, defineComponent, reactive, ref, watch} from 'vue';
import {watchDebounced} from "@vueuse/core";
import {ICONS} from '../utils/icons';
import Icon from "./Icon";
import InputSelect from "./InputSelect.vue";
import InputText from "./InputText.vue";
import Spinner from "./Spinner";
import {useTicketOutlets} from "./useTicketOutlets";
import ValleyMap from './ValleyMap';
import {buildMarkers} from "../utils/google";

const ALL_PURCHASE_TYPES_LABEL = 'All Purchase Types';
const BUY_AND_RELOAD_LABEL = 'Buy & Reload';
const RELOAD_ONLY_LABEL = 'Reload Only';

function getDeviceLocation() {
    return new Promise((resolve, reject) => {
        if(!navigator.geolocation) {
            console.error('not available');
            return reject()
        }

        navigator.geolocation.getCurrentPosition((position) => {
            resolve(position)
        }, (error) => {
            console.error('error: not available', error);
            reject()
        })
    })
}

const defaultCoordinates = {lat: 33.451284, lng: -112.07454};

export default defineComponent({
    name: 'WhereToBuy',
    components: {
        InputSelect,
        InputText,
        ValleyMap,
        Icon,
        Spinner,
    },
    setup() {
        const page = usePage();

        const urlParams = new URLSearchParams(window.location.search);

        const { outlets, isLoadingOutlets, loadOutlets } = useTicketOutlets();
        const selectedOutlet = ref();

        const outletList = computed(() => {
            if(filters.purchaseType.value === ALL_PURCHASE_TYPES_LABEL){
                filters.purchaseType.value = null;
            }
            if(!filters.purchaseType.value) {
                return outlets.value;
            }

            return outlets.value.filter(o => o.purchase_type === filters.purchaseType.value);
        });

        const defaultZoom = 13;
        const firstLoad = ref(true);
        const mapCenter = ref(defaultCoordinates);
        const mapZoom = ref(defaultZoom);
        const valleyMap = ref();
        const markers = ref([]);
        const loadingLocation = ref(false)
        const initLoad = ref(false);

        const filters = reactive({
            searchTerm: {
                label: 'Search',
                value: ref(urlParams.get('search') || '')
            },
            purchaseType: {
                label: 'Purchase type',
                value: null
            }
        })

        watchDebounced(() => filters.searchTerm.value, (searchTerm) => {
            load({ searchTerm });
        }, { debounce: 400 });

        watch(() => filters.purchaseType.value, () => {
            reloadMarkers();
        });

        watch(selectedOutlet, (to) => {
            if(to) {
                valleyMap.value.panTo(to.location);

                const marker = markers.value.find(m => m.extra_options.outlet_id === to.id);
                valleyMap.value.clearInfoWindows();
                valleyMap.value.showInfoWindow(to.id, marker.extra_options.infoWindow.content, marker.core_options.position);
            }
        });

        async function load({searchTerm = null, lat = null, lng = null} = {}) {
            if (! isLoadingOutlets.value) {
                await loadOutlets({ searchTerm })

                setupMap(lat, lng);
            }
        }

        function reloadMarkers(){
            markers.value = buildMarkers(outletList.value);
            valleyMap.value.clearInfoWindows();
        }

        function setupMap(lat = null, lng = null){
            reloadMarkers();

            if(lat && lng) {
                mapCenter.value = { lat, lng };
                mapZoom.value = defaultZoom;
            }

            if (firstLoad.value) {
                selectedOutlet.value = page.props.$nearbySearch?.selectedOutlet;
                firstLoad.value = false;
            }
        }

        function clickedMarker(marker) {
            selectedOutlet.value = outlets.value.find(o => o.id === marker.extra_options.outlet_id);
        }

        const purchaseTypes = ref([
            {
                value: null,
                label: ALL_PURCHASE_TYPES_LABEL,
            },
            {
                value: BUY_AND_RELOAD_LABEL,
                label: BUY_AND_RELOAD_LABEL
            },
            {
                value: RELOAD_ONLY_LABEL,
                label: RELOAD_ONLY_LABEL
            },
        ]);

        const boot = async () => {
            if(loadingLocation.value) {
                return;
            }

            loadingLocation.value = true;

            /*
            This small delay helps make sure screen readers will detect
            a change in content for the initial loading message
             */
            setTimeout(() => initLoad.value = true, 10)

            getDeviceLocation()
                .then(result => result.coords)
                .then(({ latitude, longitude }) => {
                    const searchTerm = urlParams.get('search');
                    if (searchTerm) {
                        load({ searchTerm });
                        return;
                    }

                    load({lat: latitude, lng: longitude});
                })
                .catch((error) => {
                    const searchTerm = urlParams.get('search');
                    if (searchTerm) {
                        load({ searchTerm });
                        return;
                    }
                    load(defaultCoordinates);
                })
                .finally(() => {
                    loadingLocation.value = false
                });
        };

        boot();

        const urlForDirections = (outlet) => {
            const query = Object.entries({
                dname: outlet.address.replace('&', '%26'),
                dlat: outlet.location.lat,
                dlng: outlet.location.lng,
            }).map(([key, value]) => `${key}=${value}`).join('&')

            return `/trip-planner?${query}`
        }

        return {
            load,
            isLoadingOutlets,
            loadingLocation,
            ICONS,
            mapCenter,
            mapZoom,
            outlets,
            markers,
            outletList,
            selectedOutlet,
            clickedMarker,
            filters,
            purchaseTypes,
            valleyMap,
            initLoad,
            urlForDirections,
        };
    }
});
</script>

<style scoped>
    .map-sidebar {
        flex: 0 0 360px;
        height: 500px;
        overflow: scroll;
    }

    .map-list {
        @apply p-1;
    }

    .map-container {
        flex: 1 1 500px;
        border: 1px solid black;
        padding: 0;
    }
</style>
